import { BaseTable } from "@/common/components/BaseTable";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ProColumns } from "@ant-design/pro-components";
import { gridName } from "@/utils/constant/gridName";
import { getTenantId } from "@/lib/storeManager";
import CustomButton from "@/common/components/common/Button";
// import { Eye } from "lucide-react";
import CommonModal from "@/common/components/common/CommonModal";
import PaymentManagementService from "@/services/FinEaseHomeAndAdmin/APIs/PaymentManagementService";
import { PaymentTransaction } from "@/utils/types/PaymentManagement";
import PaymentManagementLogModal from "./PaymentManagementLogModal";
import { CreditCard, Filter, Eye } from "lucide-react";
import FinEaseHomeRoutes from "@/projects/FinEaseHomeAndAdmin/utils/navigationRoutes";

const PaymentManagementTable = () => {
  const navigate = useNavigate();
  const tenantId = getTenantId();
  const [searchParams] = useSearchParams();
  const urlPage = parseInt(searchParams.get("page") ?? "1", 10);
  const urlPageSize = parseInt(searchParams.get("pageSize") ?? "10", 10);
  const [page, setPage] = useState(urlPage);
  const [pageSize, setPageSize] = useState(urlPageSize);
  const [showModal, setShowModal] = useState(false);
  const [paymentTransaction, setPaymentTransaction] =
    useState<PaymentTransaction | null>(null);
  const [isLoadingModal, setIsLoadingModal] = useState(false);
  const {
    data,
    isLoading,
    refetch: refetchPaymentTransactions,
  } = useQuery({
    queryKey: ["paymentTransactions", tenantId, page, pageSize],
    queryFn: () =>
      PaymentManagementService.listPaymentTransactions(page, pageSize),
    enabled: !!tenantId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 60_000,
  });

  const handleView = async (record: PaymentTransaction) => {
    if (!record?.id) {
      return;
    }

    setIsLoadingModal(true);
    try {
      // Try to fetch detailed data from API
      const response = await PaymentManagementService.getPaymentTransaction(
        record.id
      );

      if (response) {
        setPaymentTransaction(response as PaymentTransaction);
      } else {
        // Fallback to using the record data directly
        setPaymentTransaction(record);
      }
      setShowModal(true);
    } catch (error) {
      console.error("Error fetching payment transaction:", error);
      // Fallback to using the record data directly if API fails
      setPaymentTransaction(record);
      setShowModal(true);
    } finally {
      setIsLoadingModal(false);
    }
  };

  const renderCharges = (record: PaymentTransaction) => {
    const isMakeContribute = record.contribution_type === "Make Contribute";
    const isInitialNPS = record.contribution_type === "Initial NPS Contribute";

    if (isMakeContribute) {
      return (
        <div className="flex flex-col gap-1 text-sm">
          <div>
            POP Charges: ₹{parseFloat(record.pop_charges || "0").toFixed(2)}
          </div>
          <div>
            POP Charges GST: ₹
            {parseFloat(record.gst_on_pop_charges || "0").toFixed(2)}
          </div>
        </div>
      );
    }

    if (isInitialNPS) {
      return (
        <div className="flex flex-col gap-1 text-sm">
          <div>
            Account Opening Charges: ₹
            {parseFloat(record.pran_ac_opening_charges || "0").toFixed(2)}
          </div>
          <div>
            Account Opening Charges GST: ₹
            {parseFloat(record.pran_ac_opening_charges_gst || "0").toFixed(2)}
          </div>
        </div>
      );
    }

    return "-";
  };

  const columns: ProColumns<PaymentTransaction>[] = [
    {
      title: "User Details",
      dataIndex: "user",
      render: (_, record) => record.sub_fst_name ?? "-",
    },
    {
      title: "PRAN Number",
      dataIndex: "pran_number",
    },
    {
      title: "Contribution Type",
      dataIndex: "contribution_type",
    },
    {
      title: "Net Amount",
      dataIndex: "pran_total_contribution",
    },
    {
      title: "Charges",
      dataIndex: "charges",
      render: (_, record) => renderCharges(record),
    },
    {
      title: "Gross Amount",
      dataIndex: "total_payable_amount",
    },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <CustomButton
          key="view"
          icon={<Eye size={16} />}
          type="text"
          title="View"
          loading={isLoadingModal}
          onClick={() => handleView(record)}
        />,
      ],
    },
  ];

  return (
    <>
      <div className="rounded-2xl border border-border/60 bg-card text-card-foreground shadow-sm transition-shadow duration-300 hover:shadow-md">
        {/* Header */}
        <div className="flex flex-col space-y-1.5 p-6 pb-3">
          <div className="flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-base font-bold tracking-tight text-foreground">
              <CreditCard className="h-4 w-4 text-primary" />
              Payment Entries ({data?.count ?? 0})
            </h3>

            <button className="inline-flex h-9 items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-border bg-transparent px-4 text-xs font-semibold text-foreground transition-all hover:border-border/80 hover:bg-muted/50">
              <Filter className="h-4 w-4" />
              More Filters
            </button>
          </div>
        </div>

        <div className="p-6">
          <BaseTable<PaymentTransaction>
            loading={isLoading}
            columns={columns}
            dataSource={(data?.results ?? []) as PaymentTransaction[]}
            page={page}
            pageSize={pageSize}
            total={data?.count ?? 0}
            onPageChange={(page, pageSize) => {
              setPage(page);
              setPageSize(pageSize ?? 10);
              searchParams.set("page", page.toString());
              searchParams.set("pageSize", pageSize.toString());
              navigate(
                `${FinEaseHomeRoutes.PAYMENT_ENTRY}?${searchParams.toString()}`
              );
              refetchPaymentTransactions();
            }}
            rowKey="id"
            headerTitle="Recent Payment Transactions"
            toolBarRender={undefined}
            request={() => refetchPaymentTransactions()}
            tableName={gridName.activityLogsTable}
          />
        </div>
      </div>
      <CommonModal
        open={showModal}
        onCancel={() => {
          setShowModal(false);
          setPaymentTransaction(null);
        }}
        title="Payment Details"
        children={
          paymentTransaction ? (
            <PaymentManagementLogModal
              paymentTransaction={paymentTransaction}
            />
          ) : (
            <div>Loading...</div>
          )
        }
        onOk={() => {
          setShowModal(false);
          setPaymentTransaction(null);
        }}
        okText="Close"
        cancelText="Close"
        confirmLoading={false}
        footer={null}
        width={800}
        destroyOnClose
      />
    </>
  );
};

export default PaymentManagementTable;
